// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { setLocalStorage, getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import { getIsGuestUser } from '@tcp/core/src/utils/utils';
import {
  getHandlerArgs,
  isSupported,
  visibility,
} from '@tcp/web/src/components/common/molecules/PageVisibility/utils.web';
import { readCookie } from '@tcp/core/src/utils/cookie.util';
import { getSessionStorage, setSessionStorage } from '@tcp/core/src/utils';

const TIME_ZONE = 'America/New_York';
const EN_US = 'en-US';

const getShouldReloadPageAtSpecificTimeCondition = ({
  currentTimestampInEST,
  specifiedTimeToReloadInEST,
  timeToLeaveNumber,
  applicationReloadedTimeNumber,
}) => {
  return (
    currentTimestampInEST > specifiedTimeToReloadInEST &&
    (timeToLeaveNumber < specifiedTimeToReloadInEST ||
      applicationReloadedTimeNumber < specifiedTimeToReloadInEST)
  );
};

const getStorageValueInInteger = (value) => {
  return value && parseInt(value, 10);
};

const getShouldReloadPage = ({ timeToReloadPageInEST, currentTimestampInEST }) => {
  let specifiedTimeToReloadInEST;
  let shouldReloadPage;
  if (timeToReloadPageInEST) {
    specifiedTimeToReloadInEST = new Date(
      new Date().toLocaleDateString(EN_US, { timeZone: TIME_ZONE }) + timeToReloadPageInEST
    ).getTime();
  }

  if (specifiedTimeToReloadInEST) {
    const timeToLeave = getLocalStorage('timeToLeave');
    const applicationReloadedTime = getSessionStorage('applicationReloadedTime');

    const timeToLeaveNumber = getStorageValueInInteger(timeToLeave);

    const applicationReloadedTimeNumber = getStorageValueInInteger(applicationReloadedTime);

    shouldReloadPage = getShouldReloadPageAtSpecificTimeCondition({
      currentTimestampInEST,
      specifiedTimeToReloadInEST,
      timeToLeaveNumber,
      applicationReloadedTimeNumber,
    });
  }

  return shouldReloadPage;
};

const performReload = ({
  isReloading,
  userLoggedOut,
  shouldReloadPage,
  callbackFunc,
  currentTimestampInEST,
  isReloadOnInactivityPostSpecifiedTime,
}) => {
  if (isReloading || userLoggedOut || shouldReloadPage) {
    callbackFunc(isReloading || shouldReloadPage);
    if (isReloadOnInactivityPostSpecifiedTime) {
      setSessionStorage({ key: 'applicationReloadedTime', value: currentTimestampInEST });
    }
    window.location.reload();
  }
};

class PageVisibility extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSupportedState: isSupported && visibility,
    };
  }

  componentDidMount() {
    const { isSupportedState } = this.state;
    const { isReloadOnInactivity, isReloadOnInactivityPostSpecifiedTime } = this.props;
    if (!isSupportedState && (!isReloadOnInactivity || !isReloadOnInactivityPostSpecifiedTime)) {
      return;
    }

    const currentTimeInEST = new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE });
    const currentTimestampInEST = new Date(currentTimeInEST).getTime();
    if (isReloadOnInactivityPostSpecifiedTime) {
      setSessionStorage({ key: 'applicationReloadedTime', value: currentTimestampInEST });
    }

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener(visibility.event, this.handleVisibilityChange);
  }

  componentWillUnmount() {
    const { isSupportedState } = this.state;
    const { isReloadOnInactivity, isReloadOnInactivityPostSpecifiedTime } = this.props;

    if (!isSupportedState && (!isReloadOnInactivity || !isReloadOnInactivityPostSpecifiedTime)) {
      return;
    }
    document.removeEventListener(visibility.event, this.handleVisibilityChange);
  }

  handleVisibilityChange() {
    const [isVisible] = getHandlerArgs();
    const {
      callbackFunc,
      inactivityTimeInterval,
      isReloadOnInactivityPostSpecifiedTime,
      timeToReloadPageInEST,
      isReloadOnInactivity,
    } = this.props;
    const currentTimeInEST = new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE });
    const currentTimestampInEST = new Date(currentTimeInEST).getTime();

    if (isVisible) {
      let isReloading;
      let userLoggedOut;
      let shouldReloadPage;
      if (isReloadOnInactivity) {
        const timeToLeave = getLocalStorage('timeToLeave');
        isReloading = currentTimestampInEST - parseInt(timeToLeave, 10) >= inactivityTimeInterval;
        const isGuest = getIsGuestUser();
        const cid = readCookie('cid');
        userLoggedOut = !isGuest && !cid;
      }
      if (isReloadOnInactivityPostSpecifiedTime) {
        shouldReloadPage = getShouldReloadPage({
          timeToReloadPageInEST,
          currentTimestampInEST,
        });
      }

      performReload({
        isReloading,
        userLoggedOut,
        shouldReloadPage,
        callbackFunc,
        isReloadOnInactivityPostSpecifiedTime,
      });
    } else {
      // set time in local storage
      setLocalStorage({ key: 'timeToLeave', value: currentTimestampInEST });
    }
  }

  render() {
    const { children } = this.props;

    if (!children) {
      return null;
    }

    return React.Children.only(children);
  }
}

PageVisibility.defaultProps = {
  isReloadOnInactivity: false,
  isReloadOnInactivityPostSpecifiedTime: false,
  inactivityTimeInterval: 24 * 60 * 60 * 1000,
  timeToReloadPageInEST: '',
};

PageVisibility.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  isReloadOnInactivity: PropTypes.bool,
  isReloadOnInactivityPostSpecifiedTime: PropTypes.bool,
  inactivityTimeInterval: PropTypes.number,
  timeToReloadPageInEST: PropTypes.string,
};

export default PageVisibility;
